import { defaultBlocksWithSavedGeneric, ownSavedBlocks } from "./_content-blocks.ts";
import { renderSignatureBlockHtml } from "./_signature-block.ts";

// --- Asset base for outbound email images ---
const ASSET_BASE = "https://vdskjkjxyeehklwxnhrf.supabase.co/storage/v1/object/public/email-assets";

// --- Types (duplicated for edge function context) ---

interface ContentBlock {
  id: string;
  type: string;
  enabled: boolean;
  order: number;
  label?: string;
  settings?: Record<string, any>;
}

interface MeetingRecapConfig {
  version?: number;
  colors?: {
    primary?: string;
    accent?: string;
    text?: string;
    background?: string;
    headerColor?: string;
    sectionBackground?: string;
  };
  fonts?: {
    headingSize?: number;
    googleFont?: "Poppins" | "Roboto" | "Inter" | "Lato" | "Open Sans";
    family?: string;
  };
  // V1 legacy
  sections?: {
    showSummary?: boolean;
  };
  branding?: {
    logoUrl?: string;
    companyName?: string;
  };
  buttons?: {
    buttonStyle?: "rounded" | "square" | "pill";
    borderRadius?: number;
    shadowEnabled?: boolean;
    buttonText?: string;
  };
  // V2
  blocks?: ContentBlock[];
}

const googleFontMap: Record<string, string> = {
  Poppins: "'Poppins', 'Segoe UI', Arial, sans-serif",
  Roboto: "'Roboto', 'Segoe UI', Arial, sans-serif",
  Inter: "'Inter', 'Segoe UI', Arial, sans-serif",
  Lato: "'Lato', 'Segoe UI', Arial, sans-serif",
  "Open Sans": "'Open Sans', 'Segoe UI', Arial, sans-serif",
};

export interface MeetingRecapEmailParams {
  agentName: string;
  agentDescription?: string | null;
  /** GenieBranding jsonb — used by the Email Signature block. */
  agentBranding?: Record<string, any> | null;
  summary: string;
  keyTakeaways?: string[];
  keyInsights?: string[];
  actionPlan?: string[];
  decisions?: string[];
  recommendations?: string[];
  attendees?: string[];
  sentimentScore?: number;
  inboxUrl: string;
}

// --- Default block labels ---
const DEFAULT_LABELS: Record<string, string> = {
  summary: "Meeting Summary",
  attendees: "Attendees",
  key_takeaways: "Key Takeaways",
  key_insights: "Key Insights",
  decisions: "Decisions Made",
  action_plan: "Action Items",
  recommendations: "Recommendations",
  sentiment: "Meeting Sentiment",
  mobile_app_promo: "Help Genie in your pocket",
};

// --- V1 → V2 migration ---
function resolveBlocks(config: MeetingRecapConfig | null | undefined): ContentBlock[] {
  const VALUE_LAYER_AUTOINJECT: string[] = ["mobile_app_promo", "signature"];
  // Signature depends on genie branding a tenant may not have configured, so —
  // unlike the rest of the value layer — it starts OFF when backfilled.
  const VALUE_LAYER_DEFAULT_DISABLED = new Set(["signature"]);

  const sections = config?.sections || {};
  const defaults: ContentBlock[] = [
    { id: "summary", type: "summary", enabled: sections.showSummary !== false, order: 0 },
    { id: "attendees", type: "attendees", enabled: true, order: 1 },
    { id: "key_takeaways", type: "key_takeaways", enabled: true, order: 2 },
    { id: "key_insights", type: "key_insights", enabled: true, order: 3 },
    { id: "decisions", type: "decisions", enabled: true, order: 4 },
    { id: "action_plan", type: "action_plan", enabled: true, order: 5 },
    { id: "recommendations", type: "recommendations", enabled: true, order: 6 },
    { id: "sentiment", type: "sentiment", enabled: true, order: 7 },
    { id: "mobile_app_promo", type: "mobile_app_promo", enabled: true, order: 8 },
    { id: "signature", type: "signature", enabled: false, order: 9 },
  ];

  const savedBlocks = ownSavedBlocks(config, defaults);
  if (!savedBlocks) return defaultBlocksWithSavedGeneric(config, defaults);

  const blocks = [...savedBlocks].sort((a, b) => a.order - b.order);
  const existing = new Set(blocks.map((b) => b.type));
  let maxOrder = blocks.length > 0 ? Math.max(...blocks.map((b) => b.order)) : -1;

  for (const type of VALUE_LAYER_AUTOINJECT) {
    if (existing.has(type)) continue;
    maxOrder += 1;
    blocks.push({ id: type, type, enabled: !VALUE_LAYER_DEFAULT_DISABLED.has(type), order: maxOrder });
    existing.add(type);
  }

  blocks.sort((a, b) => a.order - b.order);
  return pinSignatureLast(blocks);
}

// The signature depends on genie/team branding and reads as a sign-off — it
// must always render as the final section, regardless of where `order`
// (user-editable via drag-and-drop reorder in the Report Sections UI, or a
// stale saved config) places it. Sorting by `order` alone isn't enough to
// guarantee that, so pin it last as an explicit final step.
function pinSignatureLast(blocks: ContentBlock[]): ContentBlock[] {
  const signatureIndex = blocks.findIndex((b) => b.type === "signature");
  if (signatureIndex === -1 || signatureIndex === blocks.length - 1) return blocks;
  const withoutSignature = blocks.filter((b) => b.type !== "signature");
  return [...withoutSignature, blocks[signatureIndex]];
}

// --- Style context ---
interface StyleContext {
  colors: {
    primary: string;
    accent: string;
    text: string;
    background: string;
    headerColor: string;
    sectionBackground: string;
  };
  fontFamily: string;
  borderRadius: number;
  buttonRadius: string;
  boxShadow: string;
  shadowEnabled: boolean;
}

function hexToRgba(hex: string, alpha: number): string {
  const cleanHex = hex.replace("#", "");
  const r = parseInt(cleanHex.slice(0, 2), 16);
  const g = parseInt(cleanHex.slice(2, 4), 16);
  const b = parseInt(cleanHex.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function hasContent(arr?: string[]): arr is string[] {
  return Array.isArray(arr) && arr.length > 0;
}

function sentimentLabel(score: number): { label: string; color: string } {
  if (score >= 0.7) return { label: "Positive", color: "#22C55E" };
  if (score >= 0.4) return { label: "Neutral", color: "#F59E0B" };
  return { label: "Negative", color: "#EF4444" };
}

// --- Block Renderers ---

function renderCard(label: string, contentHtml: string, styles: StyleContext): string {
  const { colors, fontFamily, buttonRadius } = styles;
  return `
    <tr>
      <td style="padding: 0 24px 10px 24px;">
        <table cellpadding="0" cellspacing="0" border="0" width="100%" style="background:#FFFFFF;border:1px solid #E5EAF0;border-radius:${buttonRadius};overflow:hidden;">
          <tr>
            <td style="height:2px;background:${colors.primary};line-height:2px;font-size:0;">&nbsp;</td>
          </tr>
          <tr>
            <td style="padding:12px 16px 10px 16px;">
              <h3 style="color:${colors.headerColor};margin:0 0 8px 0;font-size:13px;font-weight:600;font-family:${fontFamily};text-transform:uppercase;letter-spacing:0.4px;">${label}</h3>
              ${contentHtml}
            </td>
          </tr>
        </table>
      </td>
    </tr>`;
}

function renderBulletList(items: string[], styles: StyleContext): string {
  const { colors, fontFamily } = styles;
  return items
    .map(
      (item) =>
        `<div style="margin-bottom: 6px; color: ${colors.text}; font-size: 12px; font-family: ${fontFamily}; padding-left: 16px; position: relative; line-height: 1.5;"><span style="color: ${colors.primary}; font-weight: 700; position: absolute; left: 0;">&bull;</span>${escapeHtml(item)}</div>`,
    )
    .join("");
}

function renderSummary(block: ContentBlock, data: MeetingRecapEmailParams, styles: StyleContext): string {
  if (!data.summary) return "";
  const { colors, fontFamily } = styles;
  const label = block.label || DEFAULT_LABELS.summary;
  return renderCard(
    label,
    `<div style="margin: 0; color: ${colors.text}; font-size: 13px; line-height: 1.6; font-family: ${fontFamily};">${escapeHtml(data.summary)}</div>`,
    styles,
  );
}

function renderAttendees(block: ContentBlock, data: MeetingRecapEmailParams, styles: StyleContext): string {
  if (!hasContent(data.attendees)) return "";
  const { colors, fontFamily } = styles;
  const label = block.label || DEFAULT_LABELS.attendees;
  return renderCard(
    label,
    `<div style="margin: 0; color: ${colors.text}; font-size: 12px; font-family: ${fontFamily};">${escapeHtml(data.attendees.join(", "))}</div>`,
    styles,
  );
}

function renderKeyTakeaways(block: ContentBlock, data: MeetingRecapEmailParams, styles: StyleContext): string {
  if (!hasContent(data.keyTakeaways)) return "";
  const label = block.label || DEFAULT_LABELS.key_takeaways;
  return renderCard(label, renderBulletList(data.keyTakeaways, styles), styles);
}

function renderKeyInsights(block: ContentBlock, data: MeetingRecapEmailParams, styles: StyleContext): string {
  if (!hasContent(data.keyInsights)) return "";
  const label = block.label || DEFAULT_LABELS.key_insights;
  return renderCard(label, renderBulletList(data.keyInsights, styles), styles);
}

function renderDecisions(block: ContentBlock, data: MeetingRecapEmailParams, styles: StyleContext): string {
  if (!hasContent(data.decisions)) return "";
  const label = block.label || DEFAULT_LABELS.decisions;
  return renderCard(label, renderBulletList(data.decisions, styles), styles);
}

function renderActionPlan(block: ContentBlock, data: MeetingRecapEmailParams, styles: StyleContext): string {
  if (!hasContent(data.actionPlan)) return "";
  const { colors, fontFamily } = styles;
  const label = block.label || DEFAULT_LABELS.action_plan;
  const items = data.actionPlan
    .map(
      (item) => `
      <tr>
        <td style="padding-bottom:6px;">
          <table cellpadding="0" cellspacing="0" border="0" width="100%" style="background:${hexToRgba(colors.primary, 0.05)};border:1px solid ${hexToRgba(colors.primary, 0.2)};border-radius:8px;">
            <tr>
              <td style="padding:9px 12px;font-size:12px;color:${colors.text};line-height:1.5;font-family:${fontFamily};">${escapeHtml(item)}</td>
            </tr>
          </table>
        </td>
      </tr>`,
    )
    .join("");
  return renderCard(label, `<table cellpadding="0" cellspacing="0" border="0" width="100%">${items}</table>`, styles);
}

function renderRecommendations(block: ContentBlock, data: MeetingRecapEmailParams, styles: StyleContext): string {
  if (!hasContent(data.recommendations)) return "";
  const label = block.label || DEFAULT_LABELS.recommendations;
  return renderCard(label, renderBulletList(data.recommendations, styles), styles);
}

function renderSentiment(block: ContentBlock, data: MeetingRecapEmailParams, styles: StyleContext): string {
  if (data.sentimentScore === undefined) return "";
  const { fontFamily } = styles;
  const label = block.label || DEFAULT_LABELS.sentiment;
  const { label: sentLabel, color } = sentimentLabel(data.sentimentScore);
  return renderCard(
    label,
    `<span style="display: inline-block; padding: 4px 12px; border-radius: 999px; background: ${hexToRgba(color, 0.1)}; color: ${color}; font-size: 13px; font-weight: 600; font-family: ${fontFamily};">${sentLabel}</span>`,
    styles,
  );
}

function renderMobileAppPromo(block: ContentBlock, _data: MeetingRecapEmailParams, styles: StyleContext): string {
  const settings = block.settings || {};
  const { colors, fontFamily, buttonRadius } = styles;
  const heading = settings.heading || "Get Help Genie in your pocket";
  const body = settings.body || "Review meeting recaps and manage your genies on the go.";
  const appStoreUrl = settings.appStoreUrl || "https://helpgenie.ai/app";
  const playStoreUrl = settings.playStoreUrl || "https://helpgenie.ai/app";
  const mascotUrl = settings.mascotUrl !== undefined ? settings.mascotUrl : `${ASSET_BASE}/mascot-thumbsup.png`;
  const headerColor = colors.headerColor;

  const mascotCell = mascotUrl
    ? `<td valign="middle" width="60" style="padding-right: 10px;"><img src="${mascotUrl}" width="48" alt="" style="display: block; width: 48px; height: auto;" /></td>`
    : "";

  return `
    <tr>
      <td style="padding: 6px 24px 14px 24px;">
        <table cellpadding="0" cellspacing="0" border="0" width="100%" style="background: #FFFFFF; border: 1px solid #E5EAF0; border-radius: ${buttonRadius}; overflow: hidden;">
          <tr>
            <td style="height: 2px; background: ${colors.primary}; line-height: 2px; font-size: 0;">&nbsp;</td>
          </tr>
          <tr>
            <td style="padding: 12px 14px;" valign="middle">
              <table cellpadding="0" cellspacing="0" border="0" width="100%">
                <tr>
                  ${mascotCell}
                  <td valign="middle">
                    <div style="color: ${headerColor}; font-size: 14px; font-weight: 700; font-family: 'Baloo Paaji 2', ${fontFamily}; margin: 0 0 2px 0; line-height: 1.2;">${heading}</div>
                    <div style="color: #6B7280; font-size: 12px; line-height: 1.4; font-family: ${fontFamily}; margin: 0 0 10px 0;">${body}</div>
                    <table cellpadding="0" cellspacing="0" border="0">
                      <tr>
                        <td style="padding-right: 8px;">
                          <a href="${appStoreUrl}" style="display: inline-block;">
                            <img src="${ASSET_BASE}/app-store-badge.svg" alt="Download on the App Store" width="120" height="36" style="display: block; width: 120px; height: 36px; border: 0;" />
                          </a>
                        </td>
                        <td>
                          <a href="${playStoreUrl}" style="display: inline-block;">
                            <img src="${ASSET_BASE}/google-play-badge.png" alt="Get it on Google Play" width="120" height="36" style="display: block; width: 120px; height: 36px; border: 0;" />
                          </a>
                        </td>
                      </tr>
                    </table>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
        </table>
      </td>
    </tr>`;
}

function renderCustomText(block: ContentBlock, _data: MeetingRecapEmailParams, styles: StyleContext): string {
  const settings = block.settings || {};
  const title = settings.title || "";
  const body = settings.body || "";
  const alignment = settings.alignment || "left";
  if (!title && !body) return "";

  const { colors } = styles;
  const label = block.label || "Custom Section";
  const titleHtml = title
    ? `<div style="margin-bottom: 4px; font-weight: 600; color: ${colors.text}; font-size:13px;">${escapeHtml(title)}</div>`
    : "";
  const bodyHtml = body
    ? `<div style="color: ${colors.text}; font-size:12px; line-height:1.5;">${escapeHtml(body)}</div>`
    : "";

  return renderCard(label, `<div style="text-align:${alignment};">${titleHtml}${bodyHtml}</div>`, styles);
}

function renderCtaButton(block: ContentBlock, _data: MeetingRecapEmailParams, styles: StyleContext): string {
  const settings = block.settings || {};
  const url = settings.url || "#";
  const text = settings.text || "Click Here";
  const { colors, fontFamily, buttonRadius } = styles;

  return `
    <tr>
      <td style="padding: 10px 30px 25px 30px;">
        <table cellpadding="0" cellspacing="0" border="0" width="100%">
          <tr>
            <td align="center">
              <a href="${url}" style="display: inline-block; padding: 12px 28px; background-color: ${colors.primary}; color: #FFFFFF; text-decoration: none; font-weight: 600; font-size: 14px; border-radius: ${buttonRadius}; font-family: ${fontFamily}; box-shadow: ${styles.shadowEnabled ? `0 4px 12px ${hexToRgba(colors.primary, 0.3)}` : "none"};">${text}</a>
            </td>
          </tr>
        </table>
      </td>
    </tr>`;
}

function renderDivider(_block: ContentBlock, _data: MeetingRecapEmailParams, _styles: StyleContext): string {
  return `
    <tr>
      <td style="padding: 10px 30px;">
        <div style="width: 100%; height: 1px; background-color: #E5EAF0;"></div>
      </td>
    </tr>`;
}

function renderImage(block: ContentBlock, _data: MeetingRecapEmailParams, _styles: StyleContext): string {
  const settings = block.settings || {};
  const url = settings.url || "";
  const altText = settings.altText || "";
  const maxWidth = settings.maxWidth || 540;
  const alignment = settings.alignment || "center";
  if (!url) return "";

  return `
    <tr>
      <td style="padding: 10px 30px;" align="${alignment}">
        <img src="${url}" alt="${altText}" style="max-width: ${maxWidth}px; width: 100%; height: auto; display: block;" />
      </td>
    </tr>`;
}

function renderSignature(_block: ContentBlock, data: MeetingRecapEmailParams, _styles: StyleContext): string {
  const html = renderSignatureBlockHtml(data.agentName || "Your Genie", data.agentDescription, data.agentBranding);
  if (!html) return "";
  return `<tr><td style="padding: 10px 30px;">${html}</td></tr>`;
}

// --- Block Renderer Registry ---
const blockRenderers: Record<
  string,
  (block: ContentBlock, data: MeetingRecapEmailParams, styles: StyleContext) => string
> = {
  summary: renderSummary,
  attendees: renderAttendees,
  key_takeaways: renderKeyTakeaways,
  key_insights: renderKeyInsights,
  decisions: renderDecisions,
  action_plan: renderActionPlan,
  recommendations: renderRecommendations,
  sentiment: renderSentiment,
  mobile_app_promo: renderMobileAppPromo,
  signature: renderSignature,
  custom_text: renderCustomText,
  cta_button: renderCtaButton,
  divider: renderDivider,
  image: renderImage,
};

// --- Main Export ---

export function generateMeetingRecapEmail(
  params: MeetingRecapEmailParams,
  config?: MeetingRecapConfig | null,
): string {
  const { agentName, inboxUrl } = params;

  const defaults = {
    colors: {
      primary: "#4E9CFF",
      accent: "#FF4DB8",
      text: "#1A1A1A",
      background: "#F9FAFC",
      headerColor: "#28406F",
    },
    fonts: {
      family: "'Poppins', 'Segoe UI', Arial, sans-serif",
      headingSize: 32,
    },
    branding: {
      logoUrl: `${ASSET_BASE}/wordmark.png`,
      companyName: "Help Genie",
    },
    buttons: {
      buttonStyle: "rounded" as const,
      borderRadius: 8,
      shadowEnabled: true,
      buttonText: "Open in Inbox",
    },
  };

  const colors = { ...defaults.colors, ...config?.colors };
  const fonts = { ...defaults.fonts, ...config?.fonts };
  const branding = { ...defaults.branding, ...config?.branding };
  const buttons = { ...defaults.buttons, ...config?.buttons };

  const googleFontName = fonts.googleFont || "Poppins";
  const fontFamily = fonts.family || googleFontMap[googleFontName];
  const googleFontUrl = `https://fonts.googleapis.com/css2?family=${googleFontName}:wght@400;500;600;700&family=Baloo+Paaji+2:wght@700;800&display=swap`;

  const getButtonRadius = () => {
    switch (buttons.buttonStyle) {
      case "square":
        return "0px";
      case "pill":
        return "999px";
      case "rounded":
      default:
        return `${buttons.borderRadius}px`;
    }
  };

  const buttonRadius = getButtonRadius();
  const boxShadow = buttons.shadowEnabled
    ? `0 4px 12px ${hexToRgba(colors.primary || defaults.colors.primary, 0.3)}`
    : "none";

  const headerTextColor = colors.headerColor || colors.text || defaults.colors.headerColor;

  const styleContext: StyleContext = {
    colors: {
      primary: colors.primary || defaults.colors.primary,
      accent: colors.accent || defaults.colors.accent,
      text: colors.text || defaults.colors.text,
      background: colors.background || defaults.colors.background,
      headerColor: headerTextColor,
      sectionBackground: colors.sectionBackground || "#FAFBFC",
    },
    fontFamily,
    borderRadius: buttons.borderRadius || defaults.buttons.borderRadius,
    buttonRadius,
    boxShadow,
    shadowEnabled: buttons.shadowEnabled ?? defaults.buttons.shadowEnabled,
  };

  const blocks = resolveBlocks(config);
  const enabledBlocks = blocks.filter((b) => b.enabled);

  const blocksHtml = enabledBlocks
    .map((block) => {
      const renderer = blockRenderers[block.type];
      if (!renderer) return "";
      return renderer(block, params, styleContext);
    })
    .filter(Boolean)
    .join("\n");

  // The genie's own branded signature (when configured) replaces the fixed
  // Help Genie footer below rather than stacking under it.
  const hasCustomSignature = Boolean(
    enabledBlocks.some((b) => b.type === "signature") && params.agentBranding?.signatureStyle?.enabled,
  );

  const hasCtaBlock = enabledBlocks.some((b) => b.type === "cta_button");
  const inboxButtonHtml =
    inboxUrl && !hasCtaBlock
      ? `
      <tr>
        <td style="padding: 10px 30px 25px 30px;">
          <table cellpadding="0" cellspacing="0" border="0" width="100%">
            <tr>
              <td align="center">
                <a href="${inboxUrl}" style="display: inline-block; padding: 12px 28px; background-color: ${styleContext.colors.primary}; color: #FFFFFF; text-decoration: none; font-weight: 600; font-size: 14px; border-radius: ${buttonRadius}; font-family: ${fontFamily}; box-shadow: ${boxShadow};">${buttons.buttonText}</a>
              </td>
            </tr>
          </table>
        </td>
      </tr>`
      : "";

  return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Your Meeting Recap</title>
        <link rel="stylesheet" href="${googleFontUrl}" type="text/css">
        <style>@import url('${googleFontUrl}');</style>
        <!--[if mso]>
        <noscript>
          <xml>
            <o:OfficeDocumentSettings>
              <o:PixelsPerInch>96</o:PixelsPerInch>
            </o:OfficeDocumentSettings>
          </xml>
        </noscript>
        <![endif]-->
      </head>
      <body style="margin: 0; padding: 0; background-color: #F5F5F5; font-family: ${fontFamily}; -webkit-text-size-adjust: 100%; -ms-text-size-adjust: 100%;">

        <table cellpadding="0" cellspacing="0" border="0" width="100%" style="background-color: #F5F5F5; margin: 0; padding: 0;">
          <tr>
            <td align="center" style="padding: 12px;">

              <table cellpadding="0" cellspacing="0" border="0" width="600" style="max-width: 600px; background-color: ${styleContext.colors.background}; box-shadow: ${buttons.shadowEnabled ? `0 4px 20px ${hexToRgba(styleContext.colors.primary, 0.1)}` : "none"}; border-radius: ${styleContext.borderRadius}px; overflow: hidden;">

                <tr>
                  <td style="background-color: ${styleContext.colors.background}; padding: 20px 24px 4px 24px;">
                    <img src="${branding.logoUrl}" alt="${branding.companyName}" style="max-height: 28px; max-width: 160px; display: block;" />
                  </td>
                </tr>
                <tr>
                  <td style="background-color: ${styleContext.colors.background}; padding: 10px 24px 16px 24px;">
                    <h1 style="color: ${headerTextColor}; margin: 0; font-size: ${Math.max(20, (fonts.headingSize ?? 32) - 8)}px; font-weight: 700; font-family: 'Baloo Paaji 2', ${fontFamily}; line-height: 1.15; letter-spacing: -0.2px;">Your Meeting Recap</h1>
                    <div style="width: 44px; height: 2px; background: ${styleContext.colors.primary}; margin: 8px 0 0; border-radius: 2px;"></div>
                  </td>
                </tr>

                <tr>
                  <td style="padding: 0 24px 10px 24px;">
                    <table cellpadding="0" cellspacing="0" border="0" width="100%" style="background-color: ${hexToRgba(styleContext.colors.sectionBackground, 0.2)}; border: 1px solid ${styleContext.colors.sectionBackground === "#FAFBFC" ? "#E5EAF0" : styleContext.colors.sectionBackground}; border-radius: ${styleContext.borderRadius}px;">
                      <tr>
                        <td style="padding: 14px 18px; color: ${headerTextColor};">
                          <h2 style="color: ${headerTextColor}; margin: 0; font-size: 17px; font-weight: 600; font-family: ${fontFamily};">${agentName}</h2>
                          <div style="font-size: 12px; color: #6B7280; margin-top: 4px; font-family: ${fontFamily};">has finished processing your meeting</div>
                        </td>
                      </tr>
                    </table>
                  </td>
                </tr>

                ${blocksHtml}

                ${inboxButtonHtml}

                ${hasCustomSignature ? "" : `
                <tr>
                  <td align="center" style="padding: 18px 24px; background: ${styleContext.colors.primary};">
                    <div style="color: #FFFFFF; font-size: 18px; font-weight: 700; font-family: 'Baloo Paaji 2', ${fontFamily}; letter-spacing: 0.2px; margin: 0 0 4px 0;">
                      ${branding.companyName}
                    </div>
                    <div style="color: rgba(255,255,255,0.96); font-size: 12px; font-weight: 500; font-family: ${fontFamily}; margin-bottom: 10px;">
                      Your meetings, summarised
                    </div>
                    <div style="color: rgba(255,255,255,0.85); font-size: 11px; font-family: ${fontFamily};">
                      <a href="https://helpgenie.ai" style="color: #FFFFFF; text-decoration: none; font-weight: 500;">helpgenie.ai</a>
                      &nbsp;&nbsp;&middot;&nbsp;&nbsp; +64 9 886 4856
                    </div>
                  </td>
                </tr>
                `}

              </table>

            </td>
          </tr>
        </table>

      </body>
      </html>
    `;
}
